import { z } from 'zod';

import { passwordSchema } from './authSchemas';
import { COMPANY_SIZE_VALUES } from './companySchemas';
import { findCountry, isTimezoneValidForCountry } from './countries';
import { optionalText, optionalWebUrl, personName, requiredReason, requiredText } from './fieldSchemas';

/**
 * Company Registration wizard validation (CompanyRegistration.md). Shared between `willnq-web` and
 * (future) `willnq-mobile` — client-side for instant feedback only; the backend re-validates via
 * FluentValidation on receipt (docs/development/FrontendGuidelines.md §8.1). Uniqueness (company name/code,
 * corporate email, duplicate registrations) is server-side only and comes back as mapped field
 * errors from the mutation payload.
 *
 * Step 1 (the company) is the same whoever is registering. Step 2 depends on the journey: an
 * anonymous visitor describes the account to create (`ownerAccountStepSchema`), while a signed-in
 * applicant supplies nothing but consent (`existingAccountStepSchema`) because their account already
 * is the applicant.
 */

/** Wizard step 1 — the company that will be born at bootstrap (mirrors companyFormSchema fields). */
export const companyDetailsStepSchema = z.object({
  companyName: requiredText('Company name', 200),
  // An identifier: letters and digits, optionally in hyphen-separated groups ("ACME", "ACME-IN").
  // "-" or "---" identifies nothing. Mirrors `SubmitCompanyRegistrationCommandValidator.CompanyCodePattern`.
  companyCode: z
    .string()
    .trim()
    .min(1, 'Company code is required')
    .max(50, 'Code must be 50 characters or fewer')
    .regex(
      /^[A-Za-z0-9]+(-[A-Za-z0-9]+)*$/,
      'Use letters and numbers, with single hyphens between them (e.g. ACME or ACME-IN)',
    ),
  industry: optionalText('Industry', 100).default(''),
  size: z.enum(COMPANY_SIZE_VALUES),
  website: optionalWebUrl().default(''),
  country: z.string().min(1, 'Country is required').max(100),
  timezone: z.string().min(1, 'Timezone is required').max(100),
})
  // Country and time zone are one fact. Validated together here rather than trusted to the UI, so
  // the impossible pairing is rejected by the schema itself — a form bug, a stale draft, or a future
  // caller reusing this schema cannot produce a company that claims one country in another's clock.
  // The country is checked first: with an unknown country there is no zone list to judge against, and
  // reporting both would blame the time zone for the country's mistake.
  .superRefine((value, ctx) => {
    if (!findCountry(value.country)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['country'],
        message: 'Select a country from the list',
      });
      return;
    }

    if (!isTimezoneValidForCountry(value.country, value.timezone)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['timezone'],
        message: `Select a time zone used in ${value.country}`,
      });
    }
  });

/** Wizard step 2 — the applicant who becomes the founding Owner (Identity BR-6 password policy). */
export const ownerAccountStepSchema = z
  .object({
    firstName: personName('First name', 100),
    lastName: personName('Last name', 100),
    email: z.string().trim().email('Enter a valid corporate email address').max(320),
    password: passwordSchema,
    confirmPassword: z.string(),
    acceptTerms: z.boolean().refine((accepted) => accepted, {
      message: 'You must accept the terms of service to register',
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

/**
 * Wizard step 2 for a SIGNED-IN applicant. Their account already supplies the name and email, and no
 * password is involved because no account is being created — so all that is left to collect is the
 * per-registration acceptance of the terms. Kept as its own schema rather than making the fields of
 * `ownerAccountStepSchema` optional: an optional password would let the anonymous journey submit
 * without one if a caller ever reused the wrong schema.
 */
export const existingAccountStepSchema = z.object({
  acceptTerms: z.boolean().refine((accepted) => accepted, {
    message: 'You must accept the terms of service to register',
  }),
});

/** The email-verification step — same 6-digit OTP contract as Identity verification. */
export const companyRegistrationOtpSchema = z.object({
  otp: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code'),
});

/** The reviewer's rejection form — comments are mandatory (they are shown to the applicant). */
export const rejectRegistrationSchema = z.object({
  comments: requiredReason('Rejection comments', 2000),
});

export type CompanyDetailsStepForm = z.infer<typeof companyDetailsStepSchema>;
export type OwnerAccountStepForm = z.infer<typeof ownerAccountStepSchema>;
export type ExistingAccountStepForm = z.infer<typeof existingAccountStepSchema>;
export type CompanyRegistrationOtpForm = z.infer<typeof companyRegistrationOtpSchema>;
export type RejectRegistrationForm = z.infer<typeof rejectRegistrationSchema>;
